import { Divider, ListSubheader, Menu, MenuItem } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  accountMenuCurrentRestaurantStyles,
  accountMenuDividerStyles,
  accountMenuItemStyles,
  accountMenuLogoutStyles,
  accountMenuPaperStyles,
  accountMenuRestaurantStyles
} from './AccountMenu.styles'

export interface Restaurant {
  id: string | number
  name: string
  currency?: string
}

interface AccountMenuProps {
  id: string
  anchorEl: HTMLElement | null
  restaurants: Restaurant[]
  currentRestaurantId: string | null
  onClose: () => void
  onSelectRestaurant: (restaurant: Restaurant) => void
  onSettings: () => void
  onHelp: () => void
  onTour: () => void
  onLogout: () => void
}

interface AccountMenuItemProps {
  icon: string
  label: string
  onClick: () => void
  sx?: typeof accountMenuItemStyles
}

// Icons at `icon-big`, the size the sidebar's own entries use.
function AccountMenuItem(props: AccountMenuItemProps) {
  const { icon, label, onClick, sx = accountMenuItemStyles } = props
  return (
    <MenuItem onClick={onClick} sx={sx} disableRipple>
      <i className={`${icon} icon-big`} />
      <span className="body-bold-medium">{label}</span>
    </MenuItem>
  )
}

/**
 * The account button's menu, opened upwards from the foot of the sidebar the
 * way Claude's is. It is rendered inside the drawer on purpose: React counts
 * the portal as the drawer's child, so moving the pointer onto the menu does
 * not read as leaving the sidebar and fold it away under the open menu.
 *
 * The open venue's name heads the menu. An owner of several switches between
 * them here, as Claude switches organisations, so for them the heading is the
 * whole list with a tick on the open one; for everyone else it is just the
 * name, as a list of one would be a choice of one.
 */
function AccountMenu(props: AccountMenuProps) {
  const {
    id,
    anchorEl,
    restaurants,
    currentRestaurantId,
    onClose,
    onSelectRestaurant,
    onSettings,
    onHelp,
    onTour,
    onLogout
  } = props
  const { t } = useTranslation()

  const choose = (action: () => void) => () => {
    onClose()
    action()
  }

  const current = restaurants.find(
    restaurant => String(restaurant.id) === currentRestaurantId
  )

  // An array, not a fragment: MUI's Menu walks its children for keyboard
  // focus and does not look inside fragments.
  const switchable =
    restaurants.length > 1
      ? restaurants.map(restaurant => {
          const isCurrent = String(restaurant.id) === currentRestaurantId
          return (
            <MenuItem
              key={restaurant.id}
              role="menuitemradio"
              aria-checked={isCurrent}
              onClick={
                isCurrent
                  ? onClose
                  : choose(() => onSelectRestaurant(restaurant))
              }
              sx={accountMenuRestaurantStyles}
              disableRipple
            >
              <i className="las la-store icon-big" />
              <span className="body-bold-medium">{restaurant.name}</span>
              {isCurrent && <i className="las la-check icon-big" />}
            </MenuItem>
          )
        })
      : null

  // Not an action, so a subheader: MUI skips it when moving focus.
  const restaurantItems =
    switchable ??
    (current && (
      <ListSubheader sx={accountMenuCurrentRestaurantStyles}>
        {/* On a span: MUI's own subheader font would beat a class on the root. */}
        <span className="body-bold-medium">{current.name}</span>
      </ListSubheader>
    ))

  return (
    <Menu
      id={id}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: accountMenuPaperStyles } }}
    >
      {restaurantItems}
      {/* The venue above, what it lets you do below. */}
      {restaurantItems && <Divider sx={accountMenuDividerStyles} />}
      <AccountMenuItem
        icon="las la-cog"
        label={t('SETTINGS.TITLE')}
        onClick={choose(onSettings)}
      />
      <AccountMenuItem
        icon="las la-question-circle"
        label={t('SIDEBAR.FOOTER.HELP')}
        onClick={choose(onHelp)}
      />
      <AccountMenuItem
        icon="las la-info-circle"
        label={t('SIDEBAR.FOOTER.TOUR')}
        onClick={choose(onTour)}
      />
      <AccountMenuItem
        icon="las la-sign-out-alt"
        label={t('SIDEBAR.FOOTER.LOGOUT')}
        onClick={choose(onLogout)}
        sx={accountMenuLogoutStyles}
      />
    </Menu>
  )
}

export default AccountMenu
